import { useState, useEffect } from 'react';
import { plans as plansApi } from '../api/client.js';

/**
 * Modal to move a stop to another active plan.
 * @param {{ stopId: string, currentPlanId: string, onMove: (stopId: string, targetPlanId: string) => void, onCancel: () => void }} props
 */
export default function MoveStopModal({ stopId, currentPlanId, onMove, onCancel }) {
  const [plansList, setPlansList] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [loading, setLoading] = useState(true);
  const [moving, setMoving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    plansApi.list({ status: '' })
      .then((allPlans) => {
        // Filter out current plan and completed plans
        const active = allPlans.filter(
          (p) => p.id !== currentPlanId && p.status !== 'completed'
        );
        setPlansList(active);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [currentPlanId]);

  const handleSubmit = async () => {
    if (!selectedPlan) return;
    setMoving(true);
    setError('');
    try {
      await onMove(stopId, selectedPlan);
    } catch (err) {
      setError(err.message || 'Erreur lors du déplacement');
      setMoving(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('fr-FR', {
      weekday: 'short', day: 'numeric', month: 'short',
    });
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-5 w-full max-w-md shadow-xl">
        <h3 className="font-bold text-lg mb-4">Déplacer cet arrêt</h3>

        {loading ? (
          <p className="text-gray-400 text-sm py-4">Chargement des tournées...</p>
        ) : plansList.length === 0 ? (
          <p className="text-gray-400 text-sm py-4">Aucune autre tournée active disponible.</p>
        ) : (
          <div className="space-y-2 max-h-60 overflow-y-auto mb-4">
            {plansList.map((plan) => (
              <label
                key={plan.id}
                className={`block border rounded-lg p-3 cursor-pointer transition-colors ${
                  selectedPlan === plan.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                }`}
              >
                <input
                  type="radio"
                  name="targetPlan"
                  value={plan.id}
                  checked={selectedPlan === plan.id}
                  onChange={() => setSelectedPlan(plan.id)}
                  className="sr-only"
                />
                <div className="flex justify-between items-center">
                  <div>
                    <span className="font-medium text-sm">{formatDate(plan.date)}</span>
                    <span className="text-xs text-gray-500 ml-2">{plan.driver_name}</span>
                  </div>
                  <span className="text-xs text-gray-400">{plan.stop_count} arrêts</span>
                </div>
              </label>
            ))}
          </div>
        )}

        {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 border rounded-lg text-sm text-gray-600"
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedPlan || moving}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {moving ? 'Déplacement...' : 'Déplacer'}
          </button>
        </div>
      </div>
    </div>
  );
}
